import {
  type FC,
  type ReactElement,
  useCallback,
  useRef,
  useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';

import { useStore } from '@nanostores/react';
import classNames from 'classnames';
import { noop, uniqueId } from 'lodash-es';

import { segmentColors } from '@iconik-io/shared-lib/src/react/components/shared/segment/constants';
import type { SegmentEntityBase } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import { serializeUnifiedAnnotation } from '@iconik-io/shared-lib/src/utils/annotation/serialize_unified_annotation';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import type { SubclipData } from '../../ducks/asset_time_based_metadata/types';
import { useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import CheckboxBasicFieldWrapper from '../../ui/forms/field_wrappers/checkbox_basic/checkbox_basic';
import ColorOptionFieldWrapper from '../../ui/forms/field_wrappers/color_option/color_option';
import Form from '../../ui/forms/form';
import CommentColorButton from '../segment_color_button/segment_color_button';
import { SegmentInput } from '../segment_input/segment_input';
import { SubclipCollectionSelection } from '../subclip/subclip_collection_selection/subclip_collection_selection';
import { SubclipTitleText } from '../subclip/subclip_title_text';
import TooltipTrigger from '../tooltip_trigger/tooltip_trigger';
import { SEGMENT_FORM_TYPE_SUBCLIP, SEGMENT_TYPE_COMMENT } from './constants';
import type { SegmentFormType, ToolbarMode } from './types';

import fadeRightFlex from '../../styles/transitions/fade_right_flex.module.css';
import styles from './segment_form.module.css';

type FormData = Omit<SegmentEntityBase, 'is_internal'> & {
  is_internal?: 'checked';
};

type Props = {
  allowAnnotations?: boolean;
  authorizedEmail?: string;
  bottomButtons?: ReactElement;
  className?: string;
  disabled?: boolean;
  isEmailAuthorizationNeeded?: boolean;
  onChange: (segment: SegmentEntityBase) => void;
  onLayoutChanged?: () => void;
  onSubmit: (segment: SegmentEntityBase) => Promise<unknown>;
  onCancel?: () => void;
  segment: SegmentEntityBase;
  segmentFormType?: SegmentFormType;
  subclipData?: SubclipData;
  onSubclipTitleChange?: (title: string) => void;
  onSubclipCollectionIdChange?: (collectionId: string | undefined) => void;
  withColorsPicker?: boolean;
  isColorPickerExpanded?: boolean;
  verticalControls?: boolean;
  autoFocus?: boolean;
  editOrReplyMode?: boolean;
};

export const SegmentForm: FC<Props> = (props) => {
  const {
    authorizedEmail,
    className,
    disabled,
    isEmailAuthorizationNeeded,
    onChange,
    onLayoutChanged = noop,
    onSubmit,
    segment,
    segmentFormType,
    subclipData,
    onSubclipTitleChange,
    onSubclipCollectionIdChange,
    verticalControls,
    withColorsPicker,
    isColorPickerExpanded,
    autoFocus,
    editOrReplyMode,
  } = props;

  const timecodeEnabled =
    typeof segment.time_start_milliseconds === 'number' &&
    typeof segment.time_end_milliseconds === 'number';

  const { store: unifiedPlayerStore } = useBasePlayerContext();
  const [toolbarMode, setToolbarMode] = useState<ToolbarMode>();
  const personalDraftAnnotation = useStore(
    unifiedPlayerStore.segment.annotation.$personalDraftAnnotation,
  );

  const colorsPickerId = useRef(uniqueId('segment_form_color_picker_'));

  const nodeRef = useRef(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const currentSegment = useStore(unifiedPlayerStore.segment.$currentSegment);

  const handleChange = useCallback(
    (changedSegment: FormData) => {
      const segmentUpdate = Object.fromEntries(
        Object.entries(changedSegment).filter(
          ([, value]) => value !== undefined,
        ),
      );
      onChange({ ...segment, ...segmentUpdate });
    },
    [onChange, segment],
  );

  const handleColorChange = useCallback(
    (color: string | undefined) => {
      onChange({ ...segment, segment_color: color });
    },
    [onChange, segment],
  );
  const { share } = useAppSelector((state) => state.share);

  const range = currentSegment.range;
  const handleSubmit = useCallback(
    (formData: FormData, reset: (value: FormData) => void) => {
      if (!onSubmit) {
        return;
      }
      const newSegment: SegmentEntityBase = {
        ...formData,
        metadata: formData.metadata ?? {},
        has_drawing: false,
        drawing: null,
        is_internal: editOrReplyMode
          ? segment.is_internal === true
          : formData.is_internal === 'checked',
        version_id: segment.version_id,
      };
      const drawing = serializeUnifiedAnnotation(personalDraftAnnotation);
      if (drawing) {
        newSegment.drawing = drawing;
        newSegment.has_drawing = true;
      }

      if (timecodeEnabled && range) {
        newSegment.time_start_milliseconds = range.start * 1000;
        newSegment.time_end_milliseconds = range.end * 1000;
      }

      onSubmit(newSegment).then(() => {
        // Keep segment color when resetting form
        // Keep metadata_view_id and segment_type to fix submitting metadata after 1st reset
        const newValues: FormData = {
          asset_id: formData.asset_id,
          segment_color: formData.segment_color,
          segment_type: formData.segment_type,
          metadata_view_id: formData.metadata_view_id,
        };

        setToolbarMode(undefined);

        reset(newValues);
      });
    },
    [
      onSubmit,
      timecodeEnabled,
      range,
      segment.is_internal,
      segment.version_id,
      editOrReplyMode,
      personalDraftAnnotation,
    ],
  );

  const handleCommentColorButtonClick = useCallback(() => {
    setToolbarMode(
      toolbarMode === 'comment_color' ? undefined : 'comment_color',
    );
  }, [toolbarMode]);

  const shareEmailUnauthorized = Boolean(
    isEmailAuthorizationNeeded && !authorizedEmail,
  );

  const colorOption = (
    <ColorOptionFieldWrapper
      id={colorsPickerId.current}
      name="segment_color"
      options={segmentColors}
      value={segment.segment_color}
      className={styles.color_option_field}
      controlClassName={styles.color_option_control}
      onChange={handleColorChange}
    />
  );

  const colorElement = !isColorPickerExpanded ? (
    <>
      <TooltipTrigger
        tooltipText="Highlight comment"
        component={CommentColorButton}
        componentProps={{
          onClick: handleCommentColorButtonClick,
          aria: {
            label: 'Comment highlight color',
            expanded: toolbarMode === 'comment_color',
            controls: colorsPickerId.current,
          },
        }}
      />
      <CSSTransition
        nodeRef={nodeRef}
        in={toolbarMode === 'comment_color'}
        timeout={150}
        className={fadeRightFlex.hidden}
        classNames={fadeRightFlex}
      >
        <div ref={nodeRef}>{colorOption}</div>
      </CSSTransition>
    </>
  ) : (
    colorOption
  );

  const isSubclipFormType = segmentFormType === SEGMENT_FORM_TYPE_SUBCLIP;

  const handleSubmitRequested = (): void => {
    const { current } = submitButtonRef;

    // Perhaps could be changed in the future to form's requestSubmit method
    // https://caniuse.com/#feat=mdn-api_htmlformelement_requestsubmit
    current?.click();
  };

  const saveButtonLabel =
    segment.segment_type === SEGMENT_TYPE_COMMENT ? 'Comment' : 'Save';
  const defaultBottomButtons = !shareEmailUnauthorized ? (
    <Button
      ref={submitButtonRef}
      wideBasis={false}
      variant="success"
      type="submit"
      disabled={disabled || (isSubclipFormType && !subclipData?.title)}
    >
      {saveButtonLabel}
    </Button>
  ) : null;

  const bottomButtons = props.bottomButtons ?? defaultBottomButtons;

  return (
    <Form<FormData>
      className={classNames(styles.form, className)}
      onChange={handleChange}
      onValidSubmit={handleSubmit}
      vertical
      disabled={disabled}
      uiKey="segment-form"
      aria={{ label: 'Segment form' }}
    >
      <div className={styles.input_renderer}>
        {isSubclipFormType && onSubclipTitleChange ? (
          <SubclipTitleText
            title={subclipData?.title ?? ''}
            onTitleChange={onSubclipTitleChange}
          />
        ) : null}
        <SegmentInput
          disabled={disabled || shareEmailUnauthorized}
          segment={segment}
          onChange={onChange}
          onSubmitRequested={handleSubmitRequested}
          onLayoutChanged={onLayoutChanged}
          required={Object.keys(personalDraftAnnotation).length === 0}
          autoFocus={autoFocus}
        />
        {isSubclipFormType && onSubclipCollectionIdChange ? (
          <SubclipCollectionSelection
            collectionId={subclipData?.collectionId}
            onCollectionIdChange={onSubclipCollectionIdChange}
          />
        ) : null}
      </div>

      <div
        className={classNames(styles.controls, {
          [styles.vertical]: verticalControls,
          [styles.compact]:
            segment.segment_type === SEGMENT_TYPE_COMMENT &&
            !props.bottomButtons,
        })}
      >
        {!props.bottomButtons &&
        segment.segment_type !== SEGMENT_TYPE_COMMENT ? (
          <>
            <Button variant="warning" onClick={props.onCancel}>
              Cancel
            </Button>
            <div className={styles.space} />
          </>
        ) : null}
        {!editOrReplyMode && segmentFormType === 'COMMENT' && !share.id && (
          <div className={styles.internal_checkbox_wrapper}>
            <TooltipTrigger
              tooltipText="Post as internal comment. Visible to logged in users only."
              placement="left"
            >
              <CheckboxBasicFieldWrapper
                name="is_internal"
                label="Internal"
                className={styles.internal_checkbox}
                labelClassName={styles.internal_checkbox_label}
              />
            </TooltipTrigger>
          </div>
        )}
        {withColorsPicker && !shareEmailUnauthorized ? colorElement : null}
        {bottomButtons}
      </div>
    </Form>
  );
};
